import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Loader2, Send, CheckCircle, XCircle, Copy } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';

interface TelegramForm {
  botToken: string;
  chatId: string;
  webhookUrl: string;
  allowedUserIds: string;
  enabled: boolean;
  autoPostEnabled: boolean;
  autoPostFrequency: string;
  autoPostCron: string;
}

export function TelegramSettingsPage() {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<TelegramForm>({
    botToken: '',
    chatId: '',
    webhookUrl: '',
    allowedUserIds: '',
    enabled: false,
    autoPostEnabled: false,
    autoPostFrequency: 'daily',
    autoPostCron: '0 9 * * *',
  });

  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [webhookCopied, setWebhookCopied] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['telegram-settings'],
    queryFn: async () => {
      const res: any = await api.telegram.getSettings();
      return res.data;
    },
  });

  useEffect(() => {
    if (settings) {
      setForm({
        botToken: settings.botToken || '',
        chatId: settings.chatId || '',
        webhookUrl: settings.webhookUrl || '',
        allowedUserIds: settings.allowedUserIds || '',
        enabled: settings.enabled === true || settings.enabled === 'true',
        autoPostEnabled: settings.autoPostEnabled === true || settings.autoPostEnabled === 'true',
        autoPostFrequency: settings.autoPostFrequency || 'daily',
        autoPostCron: settings.autoPostCron || '0 9 * * *',
      });
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data: any) => api.telegram.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegram-settings'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.telegram.testConnection(),
    onSuccess: (data: any) => {
      setTestResult({ success: true, message: data.message || 'Connection successful!' });
    },
    onError: (error: any) => {
      setTestResult({ success: false, message: error.message || 'Connection failed' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(form);
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(form.webhookUrl);
    setWebhookCopied(true);
    setTimeout(() => setWebhookCopied(false), 2000);
  };

  const updateField = <K extends keyof TelegramForm>(key: K, value: TelegramForm[K]) => {
    setForm({ ...form, [key]: value });
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-10 w-64" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-3xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-surface-900">Telegram Bot</h1>
        <p className="text-surface-500 mt-1">Configure Telegram bot for auto-posting quotes</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Bot Configuration</h3>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-surface-700">Enable Telegram Bot</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => updateField('enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-200 rounded-full peer peer-checked:bg-primary-500 transition-colors" />
              <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
          </label>

          <Input
            label="Bot Token"
            type="password"
            value={form.botToken}
            onChange={(e) => updateField('botToken', e.target.value)}
            placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
          />

          <Input
            label="Chat ID"
            value={form.chatId}
            onChange={(e) => updateField('chatId', e.target.value)}
            placeholder="-1001234567890"
          />

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">
              Allowed User IDs
            </label>
            <Input
              value={form.allowedUserIds}
              onChange={(e) => updateField('allowedUserIds', e.target.value)}
              placeholder="123456789, 987654321"
            />
            <p className="mt-1 text-xs text-surface-400">
              Comma-separated list of Telegram user IDs allowed to interact with the bot
            </p>
          </div>
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Webhook</h3>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Webhook URL</label>
            <div className="flex gap-2">
              <Input
                value={form.webhookUrl}
                readOnly
                className="flex-1 font-mono text-sm bg-surface-50"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleCopyWebhook}
              >
                {webhookCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
              </Button>
            </div>
            <p className="mt-1 text-xs text-surface-400">
              Set this URL as the webhook in your bot settings via BotFather
            </p>
          </div>
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Auto-Post Settings</h3>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-surface-700">Enable Auto-Post</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={form.autoPostEnabled}
                onChange={(e) => updateField('autoPostEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-200 rounded-full peer peer-checked:bg-primary-500 transition-colors" />
              <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
          </label>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Frequency</label>
            <select
              value={form.autoPostFrequency}
              onChange={(e) => updateField('autoPostFrequency', e.target.value)}
              className="w-full rounded-2xl border border-surface-200 bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              <option value="hourly">Hourly</option>
              <option value="every-6h">Every 6 Hours</option>
              <option value="every-12h">Every 12 Hours</option>
              <option value="daily">Daily</option>
              <option value="custom">Custom Cron</option>
            </select>
          </div>

          {form.autoPostFrequency === 'custom' && (
            <Input
              label="Cron Expression"
              value={form.autoPostCron}
              onChange={(e) => updateField('autoPostCron', e.target.value)}
              placeholder="0 9 * * *"
            />
          )}

          <div className="p-4 rounded-xl bg-surface-50 text-sm text-surface-600">
            <p className="font-medium mb-1">Cron Expression Examples:</p>
            <ul className="space-y-1 font-mono text-xs">
              <li><code>0 9 * * *</code> — Every day at 9:00 AM</li>
              <li><code>0 */6 * * *</code> — Every 6 hours</li>
              <li><code>0 9,18 * * *</code> — Twice daily at 9 AM and 6 PM</li>
              <li><code>*/30 * * * *</code> — Every 30 minutes</li>
            </ul>
          </div>
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Test Connection</h3>
          <p className="text-sm text-surface-500">
            Send a test message to verify your bot configuration is working correctly.
          </p>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setTestResult(null);
                testMutation.mutate();
              }}
              disabled={testMutation.isPending || !form.botToken || !form.chatId}
            >
              {testMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              {testMutation.isPending ? 'Sending...' : 'Send Test Message'}
            </Button>

            {testResult && (
              <div
                className={`flex items-center gap-2 text-sm ${
                  testResult.success ? 'text-green-600' : 'text-red-500'
                }`}
              >
                {testResult.success ? <CheckCircle size={18} /> : <XCircle size={18} />}
                {testResult.message}
              </div>
            )}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
